import styled from "@emotion/styled";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import { prop } from "ramda";
import React, { useState } from "react";
import { Col, Row } from "react-flexbox-grid";
import Lottie from "react-lottie-player";

import Button from "../components/Button";
import ContentWrapper from "../components/ContentWrapper";
import Heading from "../components/Heading";
import Hero from "../components/Hero/v8";
import Icon from "../components/Icon";
import Image from "../components/ImageComponent";
import Link from "../components/Link";
import List from "../components/List";
import MuxVideo from "../components/MuxVideo";
import NewsletterForm from "../components/NewsletterForm";
import SEO from "../components/SEO";
import SocialMedia from "../components/SocialMedia";
import { bodyLarge, h4 } from "../components/Typography";
import mapBackground from "../images/map.png";
import theme from "../theme";
import { contact } from "../types";
import mq, { customMq } from "../utils/mediaQuery";
import lottieJson from "./map-pin.json";

const StyledCol = styled(Col)`
  margin-bottom: ${theme.space("m")};
  ${mq("2")} {
    margin-bottom: ${theme.space("s")};
  }
`;

const StyledLink = styled(Link)`
  color: ${theme.color("primary")};
`;

const StyledContentWrapper = styled(ContentWrapper)`
  margin: 0;
  ${customMq(768)} {
    margin: ${theme.spacing("s")} auto;
  }
`;

const Paragraph = styled.div`
  ${bodyLarge}
  margin-bottom: ${theme.space("xxs")};
  margin-top: ${theme.space("s")};
`;

const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
  z-index: 0;
  ${customMq(768)} {
    height: max(35vw, 500px);
  }

  img {
    position: absolute;
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${customMq(768)} {
    display: block;
    position: relative;
  }
`;

const ImageContainer = styled.div`
  width: 80vw;
  order: 1;

  ${customMq(768)} {
    order: 0;
    width: 50%;
    position: absolute;
    z-index: 10;
    left: 0;
  }
`;

const StyledImage = styled(Image)`
  height: 100%;
  img {
    height: 100%;
    object-fit: cover;
    object-position: 50% 50%;
  }
`;

const ContactLinks = styled.h2`
  margin: 0;
  padding: 0;
  ${bodyLarge};
  text-decoration: underline;
  font-weight: normal;

  ${mq("2")} {
    ${h4};
  }
`;

const StyledHeading = styled(Heading)`
  margin-bottom: ${theme.space("s")};
`;

const SocialLinksWrapper = styled.div`
  margin-bottom: ${theme.space("xxl")};
  > * {
    margin-right: ${theme.space("xs")};
  }
`;

const StyledRow = styled(Row)`
  ${customMq(768)} {
    margin-bottom: ${theme.space("xxl")};
  }
`;

const FormWrapper = styled.div`
  margin-bottom: ${theme.space("xl")};
`;

const StyledLottiePlayer = styled(Lottie)`
  width: 150px;
  height: 150px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-75px) translateY(-75px);
  cursor: pointer;
`;

export const ContactPage = ({
  data: {
    data: { contact, global },
  },
}) => {
  const {
    tagline,
    description,
    subDescription,
    vacancyActionText,
    vacancyActionPath,
    newsletterTitle,
    thumbnail,
    heroVideo,
    seoMetaTags,
  } = contact;

  const globalData = global;

  const facebookLink = prop("facebook", globalData);
  const instagramLink = prop("instagram", globalData);
  const twitterLink = prop("twitter", globalData);
  const linkedinLink = prop("linkedin", globalData);

  const [pinPlaying, setPinPlaying] = useState(false);
  const [pinDirection, setPinDirection] = useState(1);
  const [pinSpeed, setSpeed] = useState(1);

  const animatePin = (direction) => {
    setSpeed(direction === 1 ? 1 : 2.5);
    setPinDirection(direction);
    setPinPlaying(true);
  };

  return (
    <>
      <SEO tags={seoMetaTags} />
      <Hero title={tagline} />

      <ContentWrapper columns={12}>
        <StyledRow>
          <StyledCol xs={12} md={4}>
            <Heading size={4} as="h2">
              {description}
            </Heading>
            <Paragraph>{subDescription}</Paragraph>
            <Link to={vacancyActionPath}>
              <Button styling="link" size="small" color="secondary">
                <span>{vacancyActionText}</span>
                <Icon type="arrow" />
              </Button>
            </Link>
          </StyledCol>
          <StyledCol xs={12} mdOffset={3} md={4}>
            <ContactLinks>
              <StyledLink to={`mailto:${prop("email", globalData)}`}>
                {prop("email", globalData)}
              </StyledLink>
              <br />
              <StyledLink to={`tel:${prop("phone", globalData)}`}>
                {prop("phone", globalData)}
              </StyledLink>
            </ContactLinks>
            <Paragraph>
              <List
                items={[
                  {
                    id: "street",
                    render: <span>{prop("street", globalData)}</span>,
                  },
                  {
                    id: "city",
                    render: (
                      <span>
                        {prop("zipcode", globalData)} {prop("city", globalData)}
                      </span>
                    ),
                  },
                  {
                    id: "country",
                    render: <span>{prop("country", globalData)}</span>,
                  },
                  {
                    id: "maps",
                    render: (
                      <Link to={prop("mapsLink", globalData)}>
                        <Button styling="link" size="small" color="secondary">
                          <span>{prop("mapsText", globalData)}</span>
                        </Button>
                      </Link>
                    ),
                  },
                ]}
                vertical
              />
            </Paragraph>
          </StyledCol>
        </StyledRow>
      </ContentWrapper>

      <FlexWrapper>
        <ImageContainer>
          {heroVideo ? (
            <MuxVideo video={heroVideo} />
          ) : (
            <StyledImage image={thumbnail} />
          )}
        </ImageContainer>

        <StyledContentWrapper columns={12}>
          <Row>
            <Col s={12} mdOffset={7} md={5}>
              <StyledHeading size={3} as="h2">
                {newsletterTitle}
              </StyledHeading>
              <FormWrapper>
                <NewsletterForm
                  backgroundColor={theme.color("text.light")}
                  appearance="dark"
                  formLabels={globalData}
                />
              </FormWrapper>

              <StyledHeading size={3} as="h2">
                Volg ons op
              </StyledHeading>

              <SocialLinksWrapper>
                {facebookLink && (
                  <SocialMedia inverted type="facebook" link={facebookLink} />
                )}
                {instagramLink && (
                  <SocialMedia inverted type="instagram" link={instagramLink} />
                )}
                {twitterLink && (
                  <SocialMedia inverted type="twitter" link={twitterLink} />
                )}
                {linkedinLink && (
                  <SocialMedia inverted type="linkedin" link={linkedinLink} />
                )}
              </SocialLinksWrapper>
            </Col>
          </Row>
        </StyledContentWrapper>
      </FlexWrapper>

      <MapContainer
        onMouseEnter={() => animatePin(1)}
        onMouseLeave={() => animatePin(-1)}
      >
        <img src={mapBackground} />

        <StyledLottiePlayer
          loop={false}
          onClick={() => window.open(prop("mapsLink", globalData), "_blank")}
          onComplete={() => setPinPlaying(false)}
          animationData={lottieJson}
          play={pinPlaying}
          direction={pinDirection}
          speed={pinSpeed}
        />
      </MapContainer>
    </>
  );
};

ContactPage.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({
      contact: PropTypes.shape(contact).isRequired,
      global: PropTypes.object,
    }).isRequired,
  }).isRequired,
};

export const query = graphql`
  query getContactPage {
    data: dato {
      global {
        street
        zipcode
        city
        country
        phone
        email
        facebook
        twitter
        instagram
        linkedin
        mapsLink
        mapsText
        footerTitle
        footerTagline
        newsletterButtonText
        beforePrivacyPolicy
        afterPrivacyPolicy
        privacyPolicyLinkText
        privacyPolicyPath
      }
      contact {
        seoMetaTags: _seoMetaTags {
          tag
          content
          attributes
        }
        title
        tagline
        description
        subDescription
        vacancyActionText
        vacancyActionPath
        newsletterTitle
        thumbnail {
          data: responsiveImage(
            imgixParams: { fit: crop, w: 710, fm: jpg, auto: format }
          ) {
            ...responsiveImageFragment
          }
        }
        heroVideo {
          ...MuxVideoFragment
        }
      }
    }
  }
`;

export default ContactPage;
